import { useState, useEffect } from "react";
import DataTable from "@/components/organisms/DataTable";
import { ColumnDef } from "@tanstack/react-table";
import ColumnHeader from "@/components/atoms/ColumnHeader";
import PageHeader from "@/components/molecules/PageHeader";
import Button from "@/components/atoms/Button";
import { ArrowUpDown, Plus } from "lucide-react";
import Link from "@/components/atoms/Link";
import SearchBox from "@/components/molecules/SearchBox";
import Container from "@/components/atoms/Container";
import useAccess from "@/hooks/useAccess";
import Ticket from "@/types/Ticket";
import { Account } from "@/types/Account";
import TicketFilter from "@/components/organisms/Ticket/Filter";
import axios from "axios";
import Pagination from "@/components/atoms/Pagination";
import { X } from "lucide-react";
import useTicketTableColumnStore from "@/store/ticketTableColumn";
import useTicketFilterStore from "@/store/ticketFilterOption";
import { router } from "@inertiajs/react";
import AddLogTickets from '@/components/organisms/Ticket/AddLogs';
import ContextMenu from "@/components/molecules/ContextMenu/Index";
import LogCategory from "@/types/LogCategory";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/atoms/Accordion";
import TableSettings from "@/components/molecules/TableSettings";
import { toast } from "react-toastify";
import { formatDistanceToNow, parse } from "date-fns";
import { Switch } from "@/components/atoms/Switch";
import ReportModal from "@/components/organisms/Ticket/ExportModal";
import Settings from "@/types/Settings";

type Props = {
  initial_accounts: Account[];
  settings: Settings;
};
interface ExportFilters {
  field_name: string;
  start_date: Date;
  end_date: Date;
}

const selectOptions = [
  { id: "displayid", label: "ID" },
  { id: "subject", label: "Subject" },
  { id: "ticketStatus", label: "Status" },
  { id: "priority", label: "Priority" },
  { id: "ticketType", label: "Type" },
  { id: "function", label: "Function" },
  { id: "department", label: "Department" },
  { id: "country", label: "Country" },
  { id: "account", label: "Account" },
  { id: "internalOwner", label: "Internal Owner" },
  { id: "createdBy", label: "Created By" },
  { id: "lastActivity", label: "Last Activity" },
  { id: "createdAt", label: "Creation Date" },
];

const Index = ({ initial_accounts, settings }: Props) => {
  const [accounts, setAccounts] = useState<Account[]>(initial_accounts);
  const [tickets, setTickets] = useState<Ticket[]>([]);
  const { filters, setStorageFilter, setStorageFilters, resetStorageFilters, getStorageFilter } = useTicketFilterStore();
  const [pagination, setPagination] = useState({ page: 1, per_page: 10 });
  const [sorting, setSorting] = useState<{ column: string; direction: string | undefined }>({ column: "", direction: undefined });
  const [totalPages, setTotalPages] = useState<number>(0);
  const [totalCount, setTotalCount] = useState<number>(0);
  const [search, setSearch] = useState<string>("");
  const [showResetFilter, setshowResetFilter] = useState<boolean>(false);
  const [exportMode, setExportMode] = useState<boolean>(false);
  const [exportButtonText, setExportButtonText] = useState<string>("Export");
  const [isPaginationUpdatedInternally, setIsPaginationUpdatedInternally] = useState(false);
  const { selectedColumns, setSelectedColumns } = useTicketTableColumnStore();
  const [users, setUsers] = useState<any[]>([]);
  const [logCategories, setLogCategories] = useState<LogCategory[]>([]);
  const { currentUser, getAccess } = useAccess();
  const [appliedFilters, setAppliedFilters] = useState<{ [key: string]: any }>(filters);
  const [isReportModalOpen, setIsReportModalOpen] = useState(false);

  const allColumns: ColumnDef<Ticket>[] = [
    {
      id: "displayid",
      accessorKey: "display_id",
      header: ({ column }) => <ColumnHeader column={column} onSortChange={handleSortChange}>ID</ColumnHeader>,
      cell: ({ row }) => {
        const ticket = row.original as Ticket & { ticket_status?: { name: string } };
        const now = new Date();
    
        const parseDate = (dateString: string | null | undefined, format: string) => {
          if (!dateString) return null;
    
          try {
            const parsedDate = parse(dateString, format, new Date());
            return isNaN(parsedDate.getTime()) ? null : parsedDate;
          } catch (error) {
            return null;
          }
        };
    
        const dueDate = parseDate(ticket.due_date, "dd/MM/yyyy");
     
        const updatedAt = parseDate(ticket.updated_at, "dd/MM/yyyy HH:mm:ss");

        const getRemainingColor = (remainingDays:number) => {
          const remainingSetting = settings?.color_settings?.find(setting => setting.type === "remaining");
          if (!remainingSetting) return null;
        
          const sortedIndicators = [...remainingSetting.indicators].sort((b, a) => b.day_count - a.day_count);
         
          for (const indicator of sortedIndicators) {
            if (remainingDays <= indicator.day_count) {
              return indicator.color;
            }
          }
        
          return null;
        };
        
        const getElapsedColor = (elapsedDays:number) => {
          const elapsedSetting = settings?.color_settings?.find(setting => setting.type === "elapsed");
          if (!elapsedSetting) return null;
        
          const sortedIndicators = [...elapsedSetting.indicators].sort((a, b) => b.day_count - a.day_count);
        
          for (const indicator of sortedIndicators) {
            if (elapsedDays > indicator.day_count) {
              return indicator.color;
            }
          }
        
          return null;
        };
    
        const getColor = () => {
          const statusColor = settings?.status_colors?.find(setting => setting.status === ticket.ticket_status?.name && setting.model === "ticket")?.color;
          
          if (statusColor) {
            return statusColor;
          }
 
          if (dueDate) {
            const remainingDays = Math.ceil((dueDate.getTime() - now.getTime()) / (1000 * 60 * 60 * 24));
            
            const remainingColor = getRemainingColor(remainingDays)
           
            return remainingColor;
          }
    
          if (updatedAt) {
            const elapsedDays = Math.ceil((now.getTime() - updatedAt.getTime()) / (1000 * 60 * 60 * 24));

            const elapsedColor = getElapsedColor(elapsedDays);

            return elapsedColor;
          }
    
          return "transparent";
        };
    
        return (
          <div className="flex items-center">
            {(settings?.color_settings || settings?.status_colors) && (
              <div
                className="w-2 h-9 mr-2 rounded"
                style={{ backgroundColor: getColor() || "transparent" }}
              />
            )}
            
            <span>{ticket.display_id}</span>
          </div>
        );
      },
    },
    {
      id: "subject",
      accessorKey: "subject",
      header: ({ column }) => <ColumnHeader column={column}>Subject</ColumnHeader>,
    },
    {
      id: "ticketStatus",
      accessorKey: "ticket_status.name",
      header: ({ column }) => <ColumnHeader column={column} onSortChange={handleSortChange}>Status</ColumnHeader>,
    },
    {
      id: "priority",
      accessorKey: "priority.name",
      header: ({ column }) => <ColumnHeader column={column}>Priority</ColumnHeader>,
    },
    {
      id: "ticketType",
      accessorKey: "ticket_type.name",
      header: ({ column }) => <ColumnHeader column={column}>Type</ColumnHeader>,
    },
    {
      id: "function",
      accessorKey: "function.name",
      header: ({ column }) => <ColumnHeader column={column}>Function</ColumnHeader>,
    },
    {
      id: "department",
      accessorKey: "department.name",
      header: ({ column }) => <ColumnHeader column={column}>Department</ColumnHeader>,
    },
    {
      id: "country",
      accessorKey: "countries",
      header: ({ column }) => <ColumnHeader column={column}>Country</ColumnHeader>,
      cell: ({ row }) => {
        const countries = row.original.countries; // Access `countries` array in the row data
        return countries.map(country => country.name).join(", "); // Join all `name` fields with commas
      },
    },
    {
      id: "account",
      accessorKey: "account.name",
      header: ({ column }) => <ColumnHeader column={column} onSortChange={handleSortChange}>Account</ColumnHeader>,
      cell: ({ row }) => (
        <div className="flex items-center space-x-2">
          <img
            src={row.original.account.avatar_thumb}
            alt={row.original.account.name}
            className="w-8 h-8 rounded-full dark:bg-darkTextColor"
            loading="lazy"
          />
          <span>{row.original.account.name}</span>
        </div>
      ),
    },
    {
      id: "internalOwner",
      accessorKey: "internal_owner.name",
      header: ({ column }) => <ColumnHeader column={column} onSortChange={handleSortChange}>Internal Owner</ColumnHeader>,
      cell: ({ row }) => (
        <div className="flex items-center space-x-2">
          {row.original.internal_owner?.avatar_url && (
            <img
              src={row.original.internal_owner.avatar_url}
              alt={row.original.internal_owner.name || "User Avatar"}
              className="w-8 h-8 rounded-full dark:bg-darkTextColor"
              loading="lazy"
            />
          )}
          <span>{row.original.internal_owner?.name || ""}</span>
        </div>
      ),
    },
    {
      id: "createdBy",
      accessorKey: "created_by.name",
      header: ({ column }) => <ColumnHeader column={column} onSortChange={handleSortChange}>Created By</ColumnHeader>,
      cell: ({ row }) => (
        <div className="flex items-center space-x-2">
          {row.original.created_by?.avatar_url && (
            <img
              src={row.original.created_by.avatar_url}
              alt={row.original.created_by.name || "User Avatar"}
              className="w-8 h-8 rounded-full dark:bg-darkTextColor"
              loading="lazy"
            />
          )}
          <span>{row.original.created_by?.name || ""}</span>
        </div>
      ),
    },
    {
      id: "createdAt",
      accessorKey: "created_at",
      header: ({ column }) => <ColumnHeader column={column} onSortChange={handleSortChange}>Creation Date</ColumnHeader>,
    },
    {
      id: "lastActivity",
      accessorKey: "updated_at",
      header: ({ column }) => <ColumnHeader column={column} onSortChange={handleSortChange}>Last Activity</ColumnHeader>,
      cell: ({ row }) => {
        try {
          const updatedAt = parse(row.original.updated_at, "dd/MM/yyyy HH:mm:ss", new Date());
          const formatted = formatDistanceToNow(updatedAt, { addSuffix: true });
          return <span>{formatted}</span>;
        } catch (error) {
          console.error("Invalid date format:", row.original.updated_at, error);
          return <span>Invalid date</span>;
        }
      },
    },
  ];

  const columns = allColumns.filter(column => column.id && selectedColumns.includes(column.id));
  const [filterDataLoading, setFilterDataLoading] = useState<boolean>(true);
  
  const handleSortChange = (column: string, direction: string | undefined) => {
    setSorting({ column, direction });
  };

  useEffect(() => {
    const fetchTickets = async () => {
      const queryParams: Record<string, any> = {
        ...appliedFilters,
        ...pagination,
        column: sorting.column,
        direction: sorting.direction,
      };

      if (search) {
        queryParams.search = search;
      }

      const response = await axios.get('/tickets/listing', {
        params: queryParams
      });

      setTickets(response.data.tickets);
      setTotalCount(response.data.total_count);
      setTotalPages(response.data.total_pages);

      if (response.data.current_page !== pagination.page) {
        setPagination(prev => ({ ...prev, page: response.data.current_page }));
        setIsPaginationUpdatedInternally(true);
      } else {
        setIsPaginationUpdatedInternally(false);
      }

    };

    if (!isPaginationUpdatedInternally) {
      fetchTickets();
    }

    return () => {
      setIsPaginationUpdatedInternally(false);
    };
  }, [appliedFilters, pagination, search, sorting]);

  const breadcrumbItems = [
    {
      label: "Tickets",
      onClick: () => router.get("/tickets"),
    },
  ];

  const handleApplyFilters = () => {
    setAppliedFilters(filters);
    setPagination({ page: 1, per_page: 10 });
  };

  const handleFilterChange = (filterId: string, value: any) => {
    if (value !== null) {
      setshowResetFilter(true);
    }

    setStorageFilter(filterId as any, value);
  };

  const removeTag = (value: string) => {
    setStorageFilter('account_ids', filters.account_ids?.filter((item) => item !== value) || []);
  };

  const handlePageChange = (page: number) => {
    setPagination(prev => ({ ...prev, page }));
  };

  const handleSearch = (search: string) => {
    setshowResetFilter(true);
    setSearch(search);
    setPagination({ page: 1, per_page: 10 });
  };

  const resetFilters = () => {
    handleFilterChange('ticket_status_ids', null);
    handleFilterChange('country_ids', null);
    handleFilterChange('ticket_type_ids', null);
    handleFilterChange('internal_ticket_type_ids', null);
    handleFilterChange('function_ids', null);
    handleFilterChange('department_ids', null);

    if (currentUser && currentUser.roles.some((role: any) => role.name === 'client')) {
      handleFilterChange('account_ids', currentUser?.accounts?.map(account => account.id.toString()) || []);
      setAppliedFilters({account_ids: currentUser?.accounts?.map(account => account.id.toString()) || [], ticket_status_ids: [], country_ids: [], ticket_type_ids: [], internal_ticket_type_ids: [], function_ids: [], department_ids: []});
    } else {
      handleFilterChange('account_ids', null);
      resetStorageFilters();
      setAppliedFilters({});
    }

    setshowResetFilter(false);
    setPagination({ page: 1, per_page: 10 });
    setSearch("");
  };

  const handleExport = (exportFilters: ExportFilters) => {
    const exportTickets = async (exportFilters: ExportFilters) => {
      setExportMode(true);
      const queryParams: Record<string, any> = {
        ...filters,
        ...exportFilters
      };

      await axios.get('/tickets/export', {
        params: queryParams
      }).then(response => {
        if (response.status === 200 && response.data) {
          if (response.data.success) {
            toast.success(response.data.message || "Your export has started. You will receive an email once it's ready.");
          } else {
            toast.error(response.data.message || "An export process is already in progress. Please wait until it completes.");
          }
          //setExportButtonText('Exporting...');
          //checkExportStatus(jobId);
        }
      })
        .catch(error => {
          toast.error("Error exporting data. Please contact administrator.");
          console.error('Error exporting data:', error);
        });
    };

    exportTickets(exportFilters);
  }

  const checkExportStatus = async (jobId: string) => {
    try {
      const response = await axios.get(`/tickets/export_status/${jobId}`);

      if (response.data.status === 'complete') {
        setExportMode(false);
        setExportButtonText('Export Table');
        window.location.href = `/tickets/export_download/${jobId}`;
      } else if (response.data.status === 'failed') {
        setExportMode(false);
        setExportButtonText('Export Table');
        alert('Export failed. Please contact administrator.');
      } else {
        if (response.data.percentage) {
          setExportButtonText(`Exporting... ${response.data.percentage}%`);
        }

        setTimeout(() => checkExportStatus(jobId), 1000);
      }
    } catch (error) {
      console.error('Error checking export status:', error);
    }
  };

  useEffect(() => {
    setTimeout(() => {
      setFilterDataLoading(true);
      axios
        .get('/tickets/ticket_filters_data')
        .then(response => {
          setAccounts(response.data);
          setFilterDataLoading(false);
        })
        .catch(error => console.error('Error fetching ticket filters:', error));
    }, 400);
  
    setTimeout(() => {
      axios
        .get('/admin/users/listing?per_page=9999')
        .then(response => {
          if (response.data.users) {
            setUsers(
              response.data.users.filter(
                (user: any) =>
                  user.status === 'Active' &&
                  !user.roles.some((role: any) => role.name === 'client')
              )
            );
          }
        })
        .catch(error => console.error('Error fetching Users:', error));
    }, 300);
  
    setTimeout(() => {
      axios
        .get('/log_categories/listing')
        .then(response =>
          setLogCategories(
            response.data.filter(
              (category: any) => category.kind === 'Non-Chargeable'
            )
          )
        )
        .catch(error => console.error('Error fetching log categories:', error));
    }, 250);
  }, []);
  
  const AddLogs = ({ ticket, logCategories, users, text }: any) => {
    const isContentReady = ticket && logCategories.length > 0 && users;

    return (
      <>
        {isContentReady && (
          <ContextMenu
            content={
              <div>
                <AddLogTickets ticket={ticket} logCategories={logCategories} users={users}/>
              </div>
            }
            clickable={filterDataLoading}
          >
            <Button disabled={filterDataLoading} className="inline-flex items-center gap-2 text-black">
              <Plus size="16" />
              <span>{text}</span>
            </Button>
          </ContextMenu>
        )}
      </>
    );
  }

  const handleWatchedToggle = (checked: boolean) => {
    if (checked && currentUser) {
      setStorageFilter("watched", true);
      setAppliedFilters((prevFilters) => ({
        ...prevFilters,
        watched: true,
      }));
    } else {
      setStorageFilter("watched", false);
      setAppliedFilters((prevFilters) => ({
        ...prevFilters,
        watched: false,
      }));
    }
  
    setPagination({ page: 1, per_page: 10 });
  };

  const handleInternalOwnerToggle = (checked: boolean) => {
    if (checked && currentUser) {
      setStorageFilter("internal_owner_ids", [currentUser.id.toString()]);
      setStorageFilter("watched", appliedFilters.watched);
      setAppliedFilters((prevFilters) => ({
        ...prevFilters,
        internal_owner_ids: [currentUser.id.toString()],
      }));
    } else {
      setAppliedFilters((prevFilters) => ({
        ...prevFilters,
        internal_owner_ids: [],
      }));

      setStorageFilter("internal_owner_ids", null);
    }
  
    setPagination({ page: 1, per_page: 10 });
  };

  const handleCreatedByMeToggle = (checked: boolean) => {
    if (checked && currentUser) {
      setStorageFilter("created_by_me", true);
      setAppliedFilters((prevFilters) => ({
        ...prevFilters,
        created_by_me: true,
      }));
    } else {
      setStorageFilter("created_by_me", false);
      setAppliedFilters((prevFilters) => ({
        ...prevFilters,
        created_by_me: false,
      }));
    }
  
    setPagination({ page: 1, per_page: 10 });
  };
  
  useEffect(() => {
    if (currentUser && currentUser.roles.some((role: any) => role.name === 'client')) {
      setStorageFilter('account_ids', currentUser?.accounts?.map(account => account.id.toString()) || []);
    }
  }, [currentUser]);

  const handleExportWithFilters = (exportFilters: ExportFilters) => {
    handleExport(exportFilters);
    setIsReportModalOpen(false);
  };

  return (
    <>
      <Container>
        <PageHeader title="Tickets" breadcrumbItems={breadcrumbItems}>
        <div className="flex">
          <div className="mr-2">
            {getAccess(["create_log"]) && (
              <AddLogs ticket={{
                id: 3948,
                display_id: "Non-Chargeable",
                subject: "NONCHARGEABLE",
                internal_ticket_types: [],
              }} logCategories={logCategories} users={users} text="Non-Customer Log"/>
            )}
          </div>
          <div className="space-x-2">
            {getAccess(["create_ticket"]) ? (
              <Link href="/tickets/new">
                <Button className="inline-flex items-center gap-2 text-black">
                  <Plus size={16} /> <span>Create Ticket</span>
                </Button>
              </Link>
            ) : (
              <span className="inline-flex items-center gap-2 text-gray-500 cursor-not-allowed">
                <Button className="inline-flex items-center gap-2 text-black" disabled>
                  <Plus size={16} /> <span>Create Ticket</span>
                </Button>
              </span>
            )}
          </div>
        </div>
        </PageHeader>
        <div className="border-b border-border" />
        <div className="flex items-center justify-between mt-2">
          <div className="flex items-center gap-6 w-6/12">
            <SearchBox onSearch={handleSearch} value={search} placeholder="Search by ID OR Subject"/>
            {currentUser && (
              <div className="flex items-center gap-2">
                <Switch
                  checked={filters.watched ?? false}
                  onCheckedChange={handleWatchedToggle}
                  disabled={false}
                />
                <span className="text-sm font-medium w-32">Watched Tickets</span>
              </div>
            )}
            {currentUser && !currentUser.roles.some((role: any) => role.name === 'client') && (
              <div className="flex items-center gap-2">
                <Switch
                  checked={filters.internal_owner_ids?.includes(currentUser?.id?.toString() ?? "") ?? false}
                  onCheckedChange={handleInternalOwnerToggle}
                  disabled={false}
                />
                <span className="text-sm font-medium w-32">Assigned To Me</span>
              </div>
            )}
            {currentUser && currentUser.roles.some((role: any) => role.name === 'client') && currentUser.client_admin && (
              <div className="flex items-center gap-2">
                <Switch
                  checked={filters.created_by_me ?? false}
                  onCheckedChange={handleCreatedByMeToggle}
                  disabled={false}
                />
                <span className="text-sm font-medium w-32">Created By Me</span>
              </div>
            )}
          </div>
      
          <div className="flex gap-2 justify-end">
            <TableSettings selectOptions={selectOptions} selectedColumns={selectedColumns} setSelectedColumns={setSelectedColumns} />

            <div>
              <Button
                variant="outline"
                className="inline-flex items-center gap-2 border-border"
                onClick={() => setIsReportModalOpen(true)}
                disabled={!getAccess(["export_tickets"]) || exportMode}
              >
                <ArrowUpDown size={16} /> <span>{exportButtonText}</span>
              </Button>
            </div>
          </div>
        </div>
        
        <div className="flex items-center justify-between pt-6 pb-4">
        
        <Accordion
            className="w-full rounded-md  shadow-md"
            type="single"
            collapsible
          >
            <AccordionItem value="ticket-filter">
              <div className="relative w-full">
                <AccordionTrigger
                  className={`cursor-pointer px-4 bg-background border-b border-border rounded ${
                    filterDataLoading ? 'pointer-events-none opacity-50' : ''
                  }`}
                  onClick={e => {
                    if (filterDataLoading) {
                      e.preventDefault();
                    }
                  }}
                >
                  {filterDataLoading ? 'Filter Data Loading...' : 'Ticket Filters'}
                </AccordionTrigger>
                <AccordionContent className="w-full px-4">
                  <div className="flex flex-wrap gap-2 mt-4 mb-4 ">
                    {currentUser && !currentUser.roles.some((role: any) => role.name === 'client') && (
                      (filters.account_ids || []).map((item) => (
                        <span key={item} className="bg-gray-200 dark:bg-primary dark:text-black px-2 py-1 rounded flex items-center gap-1 text-xs">
                          {accounts.find((opt) => opt.id.toString() == item)?.name}
                          <button onClick={() => removeTag(item)}>
                            <X size={16} />
                          </button>
                        </span>
                      ))
                    )}
                  </div>

                  <TicketFilter
                    filters={[
                      {
                        id: "account_ids",
                        label: "By Account",
                        options: accounts.map(account => (
                          {
                            value: account.id,
                            label: account.name,
                            countries: account.countries,
                            statuses: account.ticket_statuses,
                            internalOwners: account.internal_owners,
                            types: account.ticket_types?.filter(type => type.status === true)
                              .map(({ id, name }) => ({
                                id,
                                name
                              })),
                            departments: account.departments?.filter(type => type.status === true)
                              .map(({ id, name }) => ({
                                id,
                                name
                              })),
                            functions: account.functions?.filter(type => type.status === true)
                              .map(({ id, name }) => ({
                                id,
                                name
                              })),
                            internalTypes: account.internal_ticket_types?.filter((type: any) => type.status === true)
                              .map(({ id, name }) => ({
                                id,
                                name
                              }))
                          }
                        )),
                        selected: filters.account_ids || [],
                      }
                    ]}
                    initialFilters={appliedFilters}
                    onFilterChange={handleFilterChange}
                    currentUser={currentUser}
                  />

                  <div className="flex">
                    <div className="flex-col ml-auto justify-end">
                      <Button className="text-black mr-2" onClick={handleApplyFilters}>Apply Filters</Button>
                      {showResetFilter && (
                        <Button className=" text-black" onClick={resetFilters}>Reset Filters</Button>
                      )} 
                    </div>
                    
           
                  </div>
                  
                </AccordionContent>
              </div>
            </AccordionItem>
          </Accordion>
          
        </div>

        <DataTable
          columns={columns}
          data={tickets}
          rowUrl={(row) => `${location.pathname}/${row.id}`}
          useBackendSorting={true}
        />
        <Pagination
          currentPage={pagination.page}
          totalPages={totalPages}
          totalCount={totalCount}
          perPage={pagination.per_page}
          onPageChange={handlePageChange}
        />
        <ReportModal
          isOpen={isReportModalOpen}
          onOpenChange={setIsReportModalOpen}
          onCreateExport={handleExportWithFilters}
        />
      </Container>
    </>
  );
};

export default Index;
